import { useState, useEffect, useRef } from 'react';
import { Shield, Loader2, AlertTriangle, X, Lock } from 'lucide-react';
import { performStepUp } from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Re-authentication modal triggered when a sensitive admin action returns
 * 401 + STEPUP_REQUIRED. Captures TOTP code (if user has MFA) or password
 * (if not), POSTs to /auth/step-up, then resolves with the new token.
 *
 * Usage:
 *   const [stepUp, setStepUp] = useState(null); // { action, resolve, reject }
 *   ...
 *   try { await sensitiveCall(); }
 *   catch (err) {
 *     if (err.code === 'STEPUP_REQUIRED') {
 *       await new Promise((resolve, reject) => setStepUp({ action: err.action, resolve, reject }));
 *       return sensitiveCall(); // retry — fetchApi now sends the fresh step-up token
 *     }
 *     throw err;
 *   }
 *   <StepUpModal request={stepUp} onClose={() => setStepUp(null)} />
 */
export default function StepUpModal({ request, onClose }) {
  const { user } = useAuth();
  // useTotp determines which credential we send. Defaults to the server's
  // record of whether the account has MFA, but the user can flip the toggle
  // (handy if the user object is stale OR if they want to use a backup code).
  const [useTotp, setUseTotp] = useState(!!user?.mfaEnabled);
  const [credential, setCredential] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (request) {
      setCredential('');
      setError(null);
      setSubmitting(false);
      setUseTotp(!!user?.mfaEnabled);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [request, user?.mfaEnabled]);

  if (!request) return null;

  const cancel = () => {
    request.reject?.(new Error('Step-up cancelled'));
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!credential.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await performStepUp(useTotp ? { totpCode: credential.trim() } : { password: credential });
      request.resolve?.();
      onClose();
    } catch (err) {
      // Backend told us MFA is required but we sent a password — switch input
      // to TOTP and prompt the user to enter a code instead of just erroring.
      const msg = err?.message || '';
      if (!useTotp && /MFA|totpCode|totp_required|MFA_REQUIRED/i.test(msg)) {
        setUseTotp(true);
        setCredential('');
        setError('This account has MFA enabled — please enter your 6-digit authenticator code.');
        setSubmitting(false);
        setTimeout(() => inputRef.current?.focus(), 50);
        return;
      }
      setError(err?.message || 'Failed to verify credential');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-2xl border border-amber-500/30 max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 px-5 py-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Confirm your identity</p>
            <p className="text-xs text-white/80 mt-0.5 truncate">{request.action || 'A sensitive action requires fresh authentication.'}</p>
          </div>
          <button onClick={cancel} className="text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-xs text-gray-400 leading-relaxed">
            For your safety, admin actions like role changes, wallet keys, and config writes require fresh authentication
            every 5 minutes — even when you're already signed in.
          </p>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-1.5">
              <Lock className="w-3 h-3" />
              {useTotp ? '6-digit code from your authenticator app' : 'Your password'}
            </label>
            <input
              ref={inputRef}
              type={useTotp ? 'text' : 'password'}
              inputMode={useTotp ? 'numeric' : 'text'}
              autoComplete={useTotp ? 'one-time-code' : 'current-password'}
              maxLength={useTotp ? 16 : 256}
              value={credential}
              onChange={e => setCredential(e.target.value)}
              placeholder={useTotp ? '123456' : '••••••••'}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none font-mono tracking-wider"
            />
            <div className="flex items-center justify-between mt-1.5">
              {useTotp ? (
                <p className="text-[10px] text-gray-500">Or paste a one-time backup code if you've lost the device.</p>
              ) : (
                <span />
              )}
              <button type="button" onClick={() => { setUseTotp(t => !t); setCredential(''); setError(null); setTimeout(() => inputRef.current?.focus(), 50); }}
                className="text-[10px] text-amber-400/80 hover:text-amber-300 underline-offset-2 hover:underline">
                Use {useTotp ? 'password' : '2FA code'} instead
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={cancel} disabled={submitting} className="btn-ghost text-xs">Cancel</button>
            <button type="submit" disabled={submitting || !credential.trim()} className="btn-primary text-xs flex items-center gap-1.5 px-4">
              {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Verifying…</> : <><Shield className="w-3 h-3" /> Confirm + retry</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
